(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory(require('core-js'), require('tone'), require('wolfy87-eventemitter')) :
  typeof define === 'function' && define.amd ? define(['core-js', 'tone', 'wolfy87-eventemitter'], factory) :
  (global = global || self, global['composer-tonejs'] = factory(null, global.Tone, global.EventEmitter));
}(this, (function (coreJs, Tone, EventEmitter) { 'use strict';

  EventEmitter = EventEmitter && Object.prototype.hasOwnProperty.call(EventEmitter, 'default') ? EventEmitter['default'] : EventEmitter;

  const guitar = 'guitar';
  const piano = 'piano';
  const flute = 'flute';
  const saxophone = 'saxophone';
  const harmonium = 'harmonium';
  /**
  * Build ADSR object from array
  * @function
  * @param {array} adsr array of numbers for adsr in the acronym order
  * @returns {object} adsr envelope object
  */

  const buildADSR = adsrArray => {
    const result = {
      attack: adsrArray[0],
      decay: adsrArray[1],
      sustain: adsrArray[2],
      release: adsrArray[3]
    };
    return result;
  };
  /**
  * Get ADSR envelope for instrument
  * @function
  * @param {object} {instrument,IADSR} options argument
  * @returns {string} {options.instrument} instrument string
  * @returns {array} {options.ADSR} ADSR array
  * @returns {boolean} {options.IADSR} inverse adsr mode
  * @returns {object} adsr object
  */

  const getEnvelope = ({
    instrument,
    ADSR,
    IADSR
  }) => {
    if (ADSR && ADSR.length === 4) {
      return buildADSR(IADSR ? ADSR.reverse() : ADSR);
    }

    if (instrument === piano) {
      const adsrArray = [0.07, 0.7, 0.05, 0.05];
      return buildADSR(IADSR ? adsrArray.reverse() : adsrArray);
    }

    if (instrument === harmonium) {
      const adsrArray = [1, 0.1, 1, 0.6];
      return buildADSR(IADSR ? adsrArray.reverse() : adsrArray);
    } // default envelope


    const adsrArray = [0.1, 0.01, 0.1, 0.1];
    return buildADSR(IADSR ? adsrArray.reverse() : adsrArray);
  };

  var instruments = /*#__PURE__*/Object.freeze({
    __proto__: null,
    guitar: guitar,
    piano: piano,
    flute: flute,
    saxophone: saxophone,
    harmonium: harmonium,
    buildADSR: buildADSR,
    getEnvelope: getEnvelope
  });

  function _defineProperty(obj, key, value) {
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }

    return obj;
  }

  const DEFAULT_SYNTH_OPTIONS = {
    envelope: {
      attack: 0.08,
      attackCurve: 'linear',
      decay: 0.3,
      decayCurve: 'exponential',
      release: 0.4,
      releaseCurve: 'exponential',
      sustain: 0.2
    }
  };
  const LOOP_TIME = 86400;
  class Player extends EventEmitter {
    // chord timing tracker.
    // original chords progression
    // for tonejs Part
    constructor(options = {}) {
      super();

      _defineProperty(this, "instrument", void 0);

      _defineProperty(this, "IADSR", void 0);

      _defineProperty(this, "loop", false);

      _defineProperty(this, "chordEndTime", 0);

      _defineProperty(this, "envelope", void 0);

      _defineProperty(this, "synth", void 0);

      _defineProperty(this, "sequence", void 0);

      _defineProperty(this, "part", void 0);

      _defineProperty(this, "bpm", void 0);

      _defineProperty(this, "timeSignature", void 0);

      _defineProperty(this, "rhythm", void 0);

      _defineProperty(this, "progressionArray", []);

      _defineProperty(this, "progressionParts", []);

      _defineProperty(this, "pressTypes", {
        verysoft: [0.1, 0.2],
        soft: [0.2, 0.4],
        medium: [0.4, 0.6],
        mediumhigh: [0.6, 0.9],
        full: [1, 1]
      });

      _defineProperty(this, "startFrom", void 0);

      this.instrument = options.instrument || piano;
      this.IADSR = options.IADSR || false;
      this.loop = options.loop || false;
      this.bpm = options.bpm || 60;
      this.envelope = getEnvelope({
        instrument: this.instrument,
        IADSR: this.IADSR
      });
    }

    play({
      note,
      chord,
      sequence,
      progression,
      arpeggiate,
      time,
      loop
    }) {
      if (note) {
        return this.playNote({
          note,
          time,
          loop
        });
      }

      if (chord) {
        return this.playChord({
          chord,
          arpeggiate,
          time,
          loop
        });
      }

      if (sequence) {
        return this.playSequence({
          sequence,
          time,
          loop
        });
      }

      if (progression) {
        return this.playProgression({
          progression,
          arpeggiate,
          time,
          loop
        });
      }

      throw new Error('No note/chord/sequence/progression passed');
    }

    playNote({
      note,
      time
    }) {
      const synth = new Tone.Synth({
        envelope: this.envelope
      }).toDestination();
      this.emit('play:note:pre', {
        note,
        time
      });
      synth.triggerAttackRelease(note, time);
      this.emit('play:note:post', {
        note,
        time
      });
      this.synth = synth;
      return this;
    }

    playChord({
      chord,
      time,
      volume
    }) {
      const polySynth = new Tone.PolySynth(Tone.Synth, {
        envelope: this.envelope
      }).toDestination();

      if (volume) {
        polySynth.set('volume', volume);
      }

      this.emit('play:chord:pre', {
        chord,
        time
      });
      polySynth.triggerAttackRelease(chord, time);
      this.emit('play:chord:post', {
        chord,
        time
      });
      this.synth = polySynth;
      return this;
    }

    playSequence({
      sequence,
      time,
      loop
    }) {
      // clear previous sequence notes becuase Tone.js maintains the state
      if (this.sequence) {
        this.sequence.dispose();
      }

      const sequenceSynth = new Tone.Synth({
        envelope: this.envelope
      }).toDestination(); // this.emit('play:sequence:pre', { note, time });

      const seq = new Tone.Sequence((sequenceTime, note) => {
        this.emit('play:sequence', {
          note,
          time,
          sequenceTime
        });
        sequenceSynth.triggerAttackRelease(note, time);
      }, sequence, time);
      const stopTime = Tone.Time(time) * sequence.length;
      seq.humanize = true;
      seq.loop = loop;
      seq.loopStart = 0;
      seq.loopEnd = Tone.Time(time) * sequence.length;
      Tone.Sequence.loop = loop;
      Tone.Transport.loop = false;
      Tone.Transport.stop();
      this.delayedStartTransport();
      const sequenceStopTime = stopTime * (loop ? LOOP_TIME : 1);
      seq.start().stop(sequenceStopTime);
      this.sequence = seq;
      return this;
    }
    /* eslint class-methods-use-this: 'off' */


    getNoteWithOctave(chordNote) {
      const {
        note,
        octave
      } = chordNote;
      return note + octave;
    }

    getNotesOfChordWithOctave(chord) {
      return chord.map(this.getNoteWithOctave);
    }

    setBPM(bpm) {
      this.bpm = bpm;
      Tone.Transport.bpm.value = bpm;
    }

    setTimeSignature(timeSignature) {
      this.timeSignature = timeSignature;
      Tone.Transport.timeSignature = timeSignature;
    }

    setProgressionEndTime({
      loop
    }) {
      this.loop = loop;
    }
    /* eslint class-methods-use-this: 'off' */


    getRandomInRange(rangeArray) {
      const min = rangeArray[0];
      const max = rangeArray[1];
      const randomNumber = Math.random() * (max - min) + min;
      return Math.round((randomNumber + Number.EPSILON) * 100) / 100;
    }

    buildArpeggioProgression({
      rhythm,
      progression,
      chordDuration,
      velocity
    }) {
      let chordNoteStartTime = 0; // total number of notes in all chords

      let noteNumber = 0;
      return progression.map((chordObject, index) => {
        const currentChordStartTime = Tone.Time(chordDuration) * index;
        const nextChordStartTime = Tone.Time(chordDuration) * (index + 1);
        const chordTime = nextChordStartTime - currentChordStartTime; // if rest

        if (chordObject.rest) {
          const partObject = {
            index,
            time: chordNoteStartTime,
            rest: true,
            chordPosition: chordObject.chordPosition
          };
          chordNoteStartTime += chordTime;
          return partObject;
        }

        const chordArpeggiateTimings = chordObject.chord.map((noteObject, noteIndex) => {
          const partObject = {
            index,
            noteIndex,
            noteNumber,
            // start time for one seq/part, see Tone.js API
            time: chordNoteStartTime,
            note: noteObject,
            chord: chordObject.chord,
            inversion: chordObject.inversion,
            quality: chordObject.quality,
            noteLength: Tone.Time(chordDuration) / chordObject.chord.length,
            velocity: this.getRandomInRange(velocity),
            chordPosition: chordObject.chordPosition
          };
          noteNumber += 1; // Note    noteLength    start-end Time
          // first chord
          // A         1000        0 - 1000
          // C         1500        1000 - 2500
          // E         500        2500 - 3000
          // second chord
          // C         100        3000 - 4000
          // E         1500         4000 - 5500
          // G         500         5500 - 6000

          if (rhythm) {
            const rhythmLength = rhythm.length;
            partObject.noteLength = rhythm[noteIndex % rhythmLength] / 1000;

            if (index === 0 && noteIndex === 0) {
              partObject.time = 0;
              this.chordEndTime = rhythm[0];
            } else {
              partObject.time = this.chordEndTime / 1000;
              this.chordEndTime += rhythm[noteIndex % rhythmLength];
            }

            return partObject;
          } // divide chord time equally for individual notes


          chordNoteStartTime += chordTime / chordObject.chord.length;
          return partObject;
        });
        return chordArpeggiateTimings;
      }).flat();
    }

    buildProgression({
      rhythm,
      progression,
      chordDuration,
      velocity
    }) {
      return progression.map((chordObject, index) => {
        const partObject = {
          index,
          // start time for one seq/part, see Tone.js API
          time: Tone.Time(chordDuration) * index,
          chord: chordObject.chord,
          inversion: chordObject.inversion,
          quality: chordObject.quality,
          noteLength: Tone.Time(chordDuration) * 1,
          velocity: this.getRandomInRange(velocity),
          rest: chordObject.rest,
          chordPosition: chordObject.chordPosition
        }; // [1000, 1500, 500];

        if (rhythm) {
          const rhythmLength = rhythm.length; // Chord    noteLength    start-end Time
          // Am         1000        0 - 1000
          // Am         1500        1000 - 2500
          // Am         500         2500 - 3000

          partObject.noteLength = rhythm[index % rhythmLength] / 1000; // 0, 0+prev+cur,

          if (index === 0) {
            partObject.time = 0;
            this.chordEndTime = rhythm[0];
          } else {
            partObject.time = this.chordEndTime / 1000;
            this.chordEndTime += rhythm[index % rhythmLength];
          }
        }

        return partObject;
      });
    }

    setSampler(samplerConfig) {
      this.samplerConfig = samplerConfig;
      this.sampler = new Tone.Sampler(samplerConfig).toDestination();
    }

    clearSampler() {
      this.samplerConfig = null;
      this.sampler = null;
    }

    initProgression({
      rhythm,
      progression,
      arpeggiate,
      chordDuration = 0.25,
      loop,
      IADSR,
      bpm = 60,
      timeSignature,
      startFrom = 0,
      velocity = 'full',
      ADSR
    }) {
      Tone.Transport.stop();
      Tone.Transport.clear();
      Tone.Transport.loop = false;
      this.progressionArray = [];
      this.progressionParts = [];
      this.startFrom = startFrom;
      this.chordDuration = chordDuration;
      this.envelope = getEnvelope({
        instrument: this.instrument,
        ADSR,
        IADSR
      });

      if (bpm) {
        this.setBPM(bpm);
      }

      if (timeSignature) {
        this.setTimeSignature(timeSignature);
      }

      this.rhythm = rhythm;
      this.progressionArray = progression;
      this.loop = loop;
      this.arpeggiate = arpeggiate;
      this.emit('play:progression:pre');

      if (arpeggiate) {
        this.progressionParts = this.buildArpeggioProgression({
          progression,
          chordDuration,
          rhythm,
          velocity: this.pressTypes[velocity]
        });
      } else {
        this.progressionParts = this.buildProgression({
          progression,
          chordDuration,
          rhythm,
          velocity: this.pressTypes[velocity]
        });
      }

      if (this.part) {
        this.part.dispose();
      }

      const progressionSynth = new Tone.PolySynth(Tone.Synth, ADSR ? {
        envelope: this.envelope
      } : DEFAULT_SYNTH_OPTIONS).toDestination();
      const {
        sampler
      } = this;
      const part = new Tone.Part((partStartTime, value) => {
        Tone.Draw.schedule(() => {
          this.emit('play:progression', { ...value,
            partStartTime
          });
        }, partStartTime); // don't play for rests

        if (value.rest) return;

        if (arpeggiate) {
          if (sampler) {
            sampler.triggerAttackRelease(this.getNoteWithOctave(value.note), chordDuration, partStartTime, value.velocity);
            return;
          }

          progressionSynth.triggerAttackRelease(this.getNoteWithOctave(value.note), chordDuration, partStartTime, value.velocity);
          return;
        }

        if (sampler) {
          sampler.triggerAttackRelease(this.getNotesOfChordWithOctave(value.chord), value.noteLength, partStartTime, value.velocity);
          return;
        }

        progressionSynth.triggerAttackRelease(this.getNotesOfChordWithOctave(value.chord), value.noteLength, partStartTime, value.velocity);
      }, this.progressionParts);
      part.start(0);
      const stopTime = Tone.Time(chordDuration).toSeconds() * this.progressionParts.length;
      part.loop = loop;
      part.loopStart = 0;

      if (rhythm) {
        part.loopEnd = this.chordEndTime / 1000;
      } else {
        part.loopEnd = Tone.Time(chordDuration).toSeconds() * this.progressionArray.length;
      }

      const partStopTime = stopTime * (this.loop ? LOOP_TIME : 1);
      part.stop(partStopTime);
      this.part = part;
    }

    clear() {
      Tone.Transport.stop();
      Tone.Transport.clear();
    }

    playProgression() {
      if (Tone.Transport.state === 'stopped' || Tone.Transport.state === 'paused') {
        Tone.start();
        Tone.Transport.start();
      }
    }

    restartProgression() {
      Tone.Transport.stop();
      this.playProgression();
      return this;
    } // start after paused


    start() {
      this.delayedStartTransport();
      return this;
    }

    pause() {
      Tone.Transport.pause();
      return this;
    }

    stopSequence() {
      if (this.sequence) {
        this.sequence.stop();
      }

      return this;
    }

    stopProgression() {
      Tone.Transport.stop();
      return this;
    }

    stop() {
      this.stopProgression();
      this.stopSequence();
      return this;
    }

  }

  const composerTone = {
    instruments,
    Player
  };

  return composerTone;

})));
